import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FaBell,
  FaBookOpen,
  FaCalendarAlt,
  FaChartLine,
  FaChalkboardTeacher,
  FaClipboardList,
  FaFileAlt,
  FaMoneyBillWave,
  FaShieldAlt,
  FaUserCircle,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppTheme } from "../context/AppThemeContext";
import { ROLE_SUPER_ADMIN, normalizeRole } from "../utils/authRole";
import AdminClassSubjectManagement from "./AdminClassSubjectManagement";
import AdminStudentManagement from "./AdminStudentManagement";
import AdminTeacherManagement from "./AdminTeacherManagement";
import AdminTimetableManagement from "./AdminTimetableManagement";
import AdminExamManagement from "./AdminExamManagement";
import AdminFeeManagement from "./AdminFeeManagement";
import AdminAdmissions from "./AdminAdmissions";
import AdminUserManagement from "./AdminUserManagement";
import AdminNotificationsManagement from "./AdminNotificationsManagement";
import AdminPermissionMatrix from "./AdminPermissionMatrix";
import AdminParentPortal from "./AdminParentPortal";
import ProctoredAssessment from "./ProctoredAssessment";

import TeacherStudentUsageView from "./TeacherStudentUsageView";
import AdmissionDetails from "../pages/AdmissionDetails";
import Profile from "../pages/Profile";
import SchoolHomeSliderManager from "../pages/SchoolHomeSliderManager";
import { ADMIN_SIDEBAR_ACTIONS } from "./AdminSidebar";
import { ADMIN_MODULE_TABS } from "../data/adminModuleTabs";

const defaultPrefs = {
  showQuickActions: true,
};

const ADMIN_SIDEBAR_ACTIVE_KEY = "adminSidebar:activeModule";

const loadStoredActivePath = (actions = []) => {
  try {
    const stored = window.localStorage.getItem(ADMIN_SIDEBAR_ACTIVE_KEY);
    if (stored && actions.some((action) => action.path === stored)) return stored;
  } catch {
    // Ignore storage failures and use the first available module.
  }
  return "";
};

const preferenceOptions = [
  { key: "showQuickActions", label: "Quick actions" },
];

const moduleActivities = ADMIN_MODULE_TABS;

const activityContentMap = {
  "/admin/class-subjects": {
    metrics: {
      title: "Metrics",
      description: "Overview of class and subject setup readiness.",
      bullets: ["Total classes and sections", "Subject coverage", "Teacher mapping health"],
    },
    classes: {
      title: "Class Directory",
      description: "Manage classes, level, sections, and class teacher assignments.",
      bullets: ["Add/edit classes inline", "Assign class teacher", "Track student count"],
    },
    subjects: {
      title: "Subject Directory",
      description: "Manage subject code, level, category, and mapped classes.",
      bullets: ["Create and edit subjects", "Map teacher to subject", "Track class usage"],
    },
  },
  "/admin/students": {
    directory: {
      title: "Directory",
      description: "Browse and manage student records with quick filters.",
      bullets: ["Search by student/class/roll", "Update core profile", "Review status tags"],
    },
    metrics: {
      title: "Metrics",
      description: "Track student module KPIs and trends.",
      bullets: ["Active students", "Review and pending cases", "Fee readiness signals"],
    },
    admission: {
      title: "Admission",
      description: "Handle admission-stage tracking and decision workflow.",
      bullets: ["Pending reviews", "Verification status", "Admission outcomes"],
    },
    fees: {
      title: "Fee Tracker",
      description: "Monitor paid, pending, and partial student payments.",
      bullets: ["Collection progress", "Pending follow-ups", "Payment state updates"],
    },
    parents: {
      title: "Parent Contacts",
      description: "Keep guardian details synchronized and actionable.",
      bullets: ["Primary contacts", "Emergency numbers", "Communication readiness"],
    },
    activity: {
      title: "Activity",
      description: "Observe usage and participation patterns by student.",
      bullets: ["Recent activity", "No-usage users", "Engagement trend"],
    },
  },
};

const loadPrefs = () => {
  try {
    const stored = localStorage.getItem("adminDashboardPrefs");
    if (!stored) return defaultPrefs;
    const parsed = JSON.parse(stored);
    return { ...defaultPrefs, ...parsed };
  } catch {
    return defaultPrefs;
  }
};

const AdminDashBoard = ({ userRole: providedUserRole, permissionRules = [], permissionReady = false }) => {
  const location = useLocation();
  const { theme, isDarkMode } = useAppTheme();
  const [prefs, setPrefs] = useState(loadPrefs);
  const [draftPrefs, setDraftPrefs] = useState(loadPrefs);
  const [showCustomize, setShowCustomize] = useState(false);
  const [activeQuickActionPath, setActiveQuickActionPath] = useState(
    () => location.state?.adminModulePath || loadStoredActivePath(ADMIN_SIDEBAR_ACTIONS)
  );
  const [activeActivityKey, setActiveActivityKey] = useState("");
  const rawUserName = localStorage.getItem("currentUserName") || "";
  const displayName = rawUserName
    ? rawUserName
        .split("@")[0]
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "Admin";
  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "Good morning";
    if (hour >= 12 && hour < 17) return "Good afternoon";
    if (hour >= 17 && hour < 21) return "Good evening";
    return "Hello";
  })();
  const userRole = providedUserRole || (() => {
    const stored = localStorage.getItem("user");
    if (!stored) return "";
    try {
      const parsed = JSON.parse(stored);
      const role = parsed?.role ?? parsed;
      return normalizeRole(typeof role === "string" ? role : String(role));
    } catch {
      return normalizeRole(stored);
    }
  })();
  const isSuperAdmin = userRole === ROLE_SUPER_ADMIN;
  const dashboardActions = isSuperAdmin
    ? [
        {
          title: "Super Admin SaaS",
          description: "Open tenant, subscription, and platform growth controls.",
          path: "/super-admin",
          icon: FaChartLine,
          tone: "bg-fuchsia-50 text-fuchsia-700",
        },
        ...ADMIN_SIDEBAR_ACTIONS,
      ]
    : ADMIN_SIDEBAR_ACTIONS;
  const activeQuickAction = dashboardActions.find((action) => action.path === activeQuickActionPath) || dashboardActions[0] || null;
  const activeActivities = activeQuickAction ? (moduleActivities[activeQuickAction.path] || []) : [];
  const selectedActivity =
    activeActivities.find((activity) => activity?.key === activeActivityKey)
    || activeActivities.find((activity) => Boolean(activity?.key))
    || null;
  const selectedActivityContent = selectedActivity
    ? activityContentMap[activeQuickAction?.path]?.[selectedActivity?.key] || null
    : null;

  useEffect(() => {
    if (!dashboardActions.length) {
      setActiveQuickActionPath("");
      setActiveActivityKey("");
      return;
    }
    if (!activeActivities.length) {
      setActiveActivityKey("");
      return;
    }
    const stillExists = activeActivities.some((activity) => activity?.key === activeActivityKey);
    if (!stillExists) {
      const firstActivityKey = activeActivities.find((activity) => Boolean(activity?.key))?.key || "";
      setActiveActivityKey(firstActivityKey);
    }
  }, [activeActivities, activeActivityKey]);

  useEffect(() => {
    localStorage.setItem("adminDashboardPrefs", JSON.stringify(prefs));
  }, [prefs]);

  useEffect(() => {
    if (showCustomize) {
      setDraftPrefs(prefs);
    }
  }, [showCustomize, prefs]);

  useEffect(() => {
    const openCustomize = () => setShowCustomize(true);
    window.addEventListener("admin-dashboard:open-customize", openCustomize);
    return () => window.removeEventListener("admin-dashboard:open-customize", openCustomize);
  }, []);

  useEffect(() => {
    const handleSelectAction = (event) => {
      const nextPath = event?.detail?.path;
      if (!nextPath || typeof nextPath !== "string") return;
      const exists = dashboardActions.some((action) => action.path === nextPath);
      if (!exists) return;

      setActiveQuickActionPath(nextPath);
      try {
        window.localStorage.setItem(ADMIN_SIDEBAR_ACTIVE_KEY, nextPath);
      } catch {
        // Keep the Dashboard selection active when storage is unavailable.
      }
    };

    window.addEventListener("admin-dashboard:select-action", handleSelectAction);
    return () => window.removeEventListener("admin-dashboard:select-action", handleSelectAction);
  }, [dashboardActions]);

  const toggleDraftPref = (key) => {
    setDraftPrefs((current) => ({ ...current, [key]: !current[key] }));
  };

  const applyCustomization = () => {
    setPrefs(draftPrefs);
    setShowCustomize(false);
    toast.success("Admin dashboard updated.");
  };

  const resetCustomization = () => {
    setDraftPrefs(defaultPrefs);
  };

  return (
    <div
      className={`h-full min-h-0 px-0 pb-4 pt-0 md:pb-8 md:pt-0 ${
        theme === "dark"
          ? "bg-slate-950 text-slate-100"
          : "bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.14),_transparent_28%),linear-gradient(180deg,_#f8fafc_0%,_#fff7ed_45%,_#f8fafc_100%)] text-slate-900"
      }`}
    >
      <div className="w-full h-full">
        <section className="relative min-h-full">
          <div className="space-y-4">

            {activeQuickAction?.path === "/admin/home-slider" ? (
              <SchoolHomeSliderManager />
            ) : activeQuickAction?.path === "/admin/class-subjects" ? (
              <AdminClassSubjectManagement />
            ) : activeQuickAction?.path === "/admin/students" ? (
              <AdminStudentManagement />
            ) : activeQuickAction?.path === "/admin/teachers" ? (
              <AdminTeacherManagement />
            ) : activeQuickAction?.path === "/admin/timetable" ? (
              <AdminTimetableManagement />
            ) : activeQuickAction?.path === "/admin/exams" ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
                <FaClipboardList className="mx-auto mb-4 text-4xl text-slate-300" />
                <h3 className="text-lg font-bold text-slate-700">Exam Management Disabled</h3>
                <p className="mt-2 text-sm text-slate-500">Exam Management is currently disabled. Contact the super-admin to re-enable this module.</p>
              </div>
            ) : activeQuickAction?.path === "/admin/fees" ? (
              <AdminFeeManagement />
            ) : activeQuickAction?.path === "/admin/admissions" ? (
              <AdminAdmissions />
            ) : activeQuickAction?.path === "/admin/users" ? (
              <AdminUserManagement />
            ) : activeQuickAction?.path === "/admin/students-activity" ? (
              <TeacherStudentUsageView />
            ) : activeQuickAction?.path === "/admission-details" ? (
              <AdmissionDetails userRole={userRole} embedded />
            ) : activeQuickAction?.path === "/admin/notifications" ? (
              <AdminNotificationsManagement />
            ) : activeQuickAction?.path === "/admin/permissions" ? (
              <AdminPermissionMatrix />
            ) : activeQuickAction?.path === "/admin/parent-portal" ? (
              <AdminParentPortal />
            ) : activeQuickAction?.path === "/admin/assessments" ? (
              <div className="rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm">
                <ProctoredAssessment creatorRole="admin" creatorName="Admin" />
              </div>
            ) : activeQuickAction?.path === "/profile-inline" ? (
              <Profile embedded />
            ) : activeQuickAction ? (
              <div className={`relative overflow-hidden border px-5 py-6 sm:px-7 sm:py-8 ${isDarkMode ? "border-slate-800 bg-[linear-gradient(145deg,#111827_0%,#020617_78%)]" : "border-slate-200 bg-[linear-gradient(145deg,#ffffff_0%,#ecfeff_78%)]"}`}>
                <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-cyan-500/15 blur-3xl" />
                <div className="relative">
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${isDarkMode ? "text-cyan-300/80" : "text-cyan-700/80"}`}>
                    Activity menu
                  </p>
                  <h3 className="mt-3 text-2xl font-black leading-tight sm:text-3xl">
                    {activeQuickAction.title}
                  </h3>
                </div>

                {activeActivities.length > 0 ? (
                  <div className="relative mt-6 grid gap-4 lg:grid-cols-[250px_1fr]">
                    <div className="space-y-2">
                      {activeActivities.map((activity) => (
                        <button
                          key={`${activeQuickAction.path}-${activity.key}`}
                          type="button"
                          onClick={() => setActiveActivityKey(activity.key)}
                          className={`block w-full border px-4 py-3 text-left text-sm font-semibold transition ${selectedActivity?.key === activity.key ? "border-cyan-500 bg-cyan-600 text-white" : (isDarkMode ? "border-slate-800 bg-slate-900 text-slate-200 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")}`}
                        >
                          {activity.label}
                        </button>
                      ))}
                    </div>

                    <div className={`border p-5 ${isDarkMode ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-white/80"}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Selected activity content</p>
                      <h4 className="mt-2 text-xl font-bold">{selectedActivityContent?.title || selectedActivity?.label || "Activity"}</h4>
                      <p className={`mt-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {selectedActivityContent?.description || "Details for the selected activity are shown here."}
                      </p>
                      <div className="mt-4 space-y-2">
                        {(selectedActivityContent?.bullets || []).map((item) => (
                          <div
                            key={item}
                            className={`border-l-2 pl-3 text-sm ${isDarkMode ? "border-cyan-500/40 text-slate-200" : "border-cyan-600/40 text-slate-700"}`}
                          >
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`relative mt-6 border p-4 ${isDarkMode ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-white/80"}`}>
                    <p className="text-sm font-semibold">{activeQuickAction.description}</p>
                    <p className={`mt-2 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No activity menu configured for this module yet.</p>
                  </div>
                )}
              </div>
            ) : (
              <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No modules available.</p>
            )}
          </div>
        </section>
      </div>

      {showCustomize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
          <div className={`w-full max-w-xl rounded-3xl border p-6 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Customize</p>
                <h2 className="mt-2 text-2xl font-bold">Choose admin dashboard sections</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowCustomize(false)}
                className={`rounded-full px-3 py-1 text-sm font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {preferenceOptions.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => toggleDraftPref(option.key)}
                  className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${draftPrefs[option.key]
                    ? isDarkMode
                      ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                    : isDarkMode
                      ? "border-slate-800 bg-slate-950 text-slate-300"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                >
                  <span className="font-semibold">{option.label}</span>
                  <span className={`inline-flex h-6 w-10 items-center rounded-full p-1 transition ${draftPrefs[option.key] ? "bg-amber-500" : isDarkMode ? "bg-slate-800" : "bg-slate-300"}`}>
                    <span className={`h-4 w-4 rounded-full bg-white transition ${draftPrefs[option.key] ? "translate-x-4" : "translate-x-0"}`} />
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={applyCustomization}
                className="rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
              >
                Apply changes
              </button>
              <button
                type="button"
                onClick={resetCustomization}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                Reset defaults
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashBoard;
